import { useCallback, useEffect, useState } from "react";
import { RoutePanel } from "@/components/RoutePanel";
import { PlanPanel } from "@/components/PlanPanel";
import { Link } from "react-router-dom";
import { Truck, MessageCircle, FileText, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { PageHeader, DateNav, Money, StatusPill, AuthImage, Empty, NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { todayIST, itemsText, fmtDateTime, waLink, msgs, mapsHref } from "@/lib/format";

export default function Deliveries() {
  const [date, setDate] = useState(todayIST());
  const [data, setData] = useState(null);
  const [boys, setBoys] = useState([]);
  const [bulkBoy, setBulkBoy] = useState("");
  const [photo, setPhoto] = useState(null);
  const [biz, setBiz] = useState("");
  const load = useCallback(() => api.get("/deliveries", { params: { date } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [date]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    api.get("/users", { params: { role: "delivery" } }).then((r) => { setBoys(r.data); setBulkBoy(r.data[0]?.id || ""); });
    api.get("/settings").then((r) => setBiz(r.data.business_name));
  }, []);

  const assign = async (ids, boyId) => {
    try { await api.post("/deliveries/assign", { order_ids: ids, delivery_boy_id: boyId || null }); toast.success("Assigned"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const orders = data?.orders || [];
  const unassigned = orders.filter((o) => !o.delivery_boy_id && o.delivery_status !== "delivered").map((o) => o.id);
  const count = (s) => orders.filter((o) => o.delivery_status === s).length;

  return (
    <div data-testid="deliveries-page">
      <PageHeader eyebrow="Door-to-door" title="Delivery route" subtitle="Delivery boys upload a photo at each doorstep. Customers confirm receipt from their WhatsApp link.">
        <DateNav value={date} onChange={setDate} />
        <Button variant="outline" asChild><Link to="/delivery" data-testid="open-delivery-app"><Smartphone className="mr-1 h-4 w-4" />Delivery app</Link></Button>
      </PageHeader>
      <PlanPanel date={date} onPlanned={load} />
      <RoutePanel key={`r-${date}-${data?.orders?.length || 0}`} date={date} />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5 mb-6">
        {[["Stops", orders.length], ["Delivered", count("delivered")], ["Pending", count("pending")], ["Failed", count("failed")], ["Customer confirmed", orders.filter((o) => o.customer_confirmation === "received").length]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`delivery-count-${l.toLowerCase().replace(" ", "-")}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div><div className="font-mono text-2xl font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>
      {unassigned.length > 0 && boys.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="bulk-assign-bar">
          <span className="text-sm font-medium">{unassigned.length} stops unassigned. Assign all to</span>
          <NativeSelect className="w-48" value={bulkBoy} onChange={(e) => setBulkBoy(e.target.value)} data-testid="bulk-assign-select">
            {boys.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </NativeSelect>
          <Button size="sm" className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => assign(unassigned, bulkBoy)} data-testid="bulk-assign-btn">Assign</Button>
        </div>
      )}
      {data && orders.length === 0 && <Empty icon={Truck} title="No deliveries for this date" text="Confirmed orders for the date appear here as route stops." testid="deliveries-empty" />}
      <div className="space-y-3">
        {orders.map((o, i) => (
          <div key={o.id} className="grid gap-3 rounded-2xl border border-[#E7E2D6] bg-white p-4 md:grid-cols-[40px_1.3fr_1fr_160px_auto] md:items-center" data-testid={`delivery-row-${o.id}`}>
            <div className="grid h-9 w-9 place-items-center rounded-full bg-[#FAF4E8] font-mono text-sm font-semibold">{i + 1}</div>
            <div>
              <div className="font-semibold">{o.customer_name}</div>
              <div className="text-xs text-muted-foreground">{o.area} · {o.address}</div>
              <a href={mapsHref(o)} target="_blank" rel="noreferrer" className={`text-xs font-medium hover:underline ${o.lat != null ? "text-emerald-700" : "text-rose-600"}`} data-testid={`delivery-maps-${o.id}`}>{o.lat != null ? "Open in Maps" : "No map pin. Search address"}</a>
              <div className="text-sm mt-1">{itemsText(o.items)} · <Money value={o.total} className="font-semibold" /></div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={o.delivery_status} testid={`delivery-status-${o.id}`} />
              {o.customer_confirmation && <StatusPill status={o.customer_confirmation} label={o.customer_confirmation === "received" ? "Customer confirmed" : "Issue"} />}
              {o.delivered_at && <span className="text-xs text-muted-foreground">{fmtDateTime(o.delivered_at)}</span>}
              {o.cash_collected > 0 && <span className="text-xs text-emerald-700">Collected <Money value={o.cash_collected} /></span>}
              {o.fail_reason && <span className="text-xs text-rose-600">{o.fail_reason}</span>}
              {o.customer_note && <span className="text-xs text-rose-600">"{o.customer_note}"</span>}
            </div>
            <NativeSelect value={o.delivery_boy_id || ""} disabled={o.delivery_status === "delivered"} onChange={(e) => assign([o.id], e.target.value)} data-testid={`assign-select-${o.id}`}>
              <option value="">Unassigned</option>
              {boys.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </NativeSelect>
            <div className="flex items-center gap-2">
              {o.photo_path && <button onClick={() => setPhoto(o)} data-testid={`view-photo-${o.id}`}><AuthImage path={`/deliveries/${o.id}/photo`} className="h-12 w-12 rounded-lg" /></button>}
              {o.delivery_status === "delivered" && (
                <>
                  <Button size="icon" className="bg-[#25D366] hover:bg-[#20bd5a] text-white" onClick={() => window.open(waLink(o.customer_phone, msgs.delivery({ name: o.customer_name, token: o.customer_token }, biz)), "_blank")} data-testid={`wa-delivery-${o.id}`}><MessageCircle className="h-4 w-4" /></Button>
                  <Button size="icon" variant="outline" asChild><Link to={`/invoice/${o.id}`} data-testid={`invoice-${o.id}`}><FileText className="h-4 w-4" /></Link></Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!photo} onOpenChange={(o) => !o && setPhoto(null)}>
        <DialogContent className="max-w-lg p-2" data-testid="photo-dialog">
          {photo && <AuthImage path={`/deliveries/${photo.id}/photo`} className="w-full max-h-[75vh] rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
